/**
 * Token reference for Databricks SQL
 * Based on the ANTLR parser but simplified for Monaco tokenization
 */

import {
  BUILT_IN_FUNCTION_NAMES,
  TABLE_VALUED_FUNCTION_NAMES,
} from './builtInFunctionNames';
import { PRIMITIVE_TYPES, COMPOSITE_TYPES } from './dataTypes';

// Core SQL keywords
const sqlKeywords = [
  'ADD',
  'AFTER',
  'ALL',
  'ALTER',
  'ANALYZE',
  'AND',
  'ANTI',
  'ANY',
  'ARCHIVE',
  'ARRAY',
  'AS',
  'ASC',
  'AT',
  'AUTHORIZATION',
  'BETWEEN',
  'BOTH',
  'BUCKET',
  'BUCKETS',
  'BY',
  'CACHE',
  'CASCADE',
  'CASE',
  'CAST',
  'CATALOG',
  'CATALOGS',
  'CHANGE',
  'CHECK',
  'CLEAR',
  'CLUSTER',
  'CLUSTERED',
  'CODEGEN',
  'COLLATE',
  'COLLECTION',
  'COLUMN',
  'COLUMNS',
  'COMMENT',
  'COMMIT',
  'COMPACT',
  'COMPACTIONS',
  'COMPUTE',
  'CONCATENATE',
  'CONSTRAINT',
  'COST',
  'CREATE',
  'CROSS',
  'CUBE',
  'CURRENT',
  'CURRENT_DATE',
  'CURRENT_TIME',
  'CURRENT_TIMESTAMP',
  'CURRENT_USER',
  'DATA',
  'DATABASE',
  'DATABASES',
  'DAY',
  'DBPROPERTIES',
  'DEFINED',
  'DELETE',
  'DELIMITED',
  'DELTA',
  'DESC',
  'DESCRIBE',
  'DFS',
  'DIRECTORIES',
  'DIRECTORY',
  'DISTINCT',
  'DISTRIBUTE',
  'DIV',
  'DROP',
  'ELSE',
  'END',
  'ESCAPE',
  'ESCAPED',
  'EXCEPT',
  'EXCHANGE',
  'EXISTS',
  'EXPLAIN',
  'EXPORT',
  'EXTENDED',
  'EXTERNAL',
  'EXTRACT',
  'FALSE',
  'FETCH',
  'FIELDS',
  'FILTER',
  'FILEFORMAT',
  'FIRST',
  'FOLLOWING',
  'FOR',
  'FOREIGN',
  'FORMAT',
  'FORMATTED',
  'FROM',
  'FULL',
  'FUNCTION',
  'FUNCTIONS',
  'GLOBAL',
  'GRANT',
  'GROUP',
  'GROUPING',
  'HAVING',
  'HOUR',
  'IF',
  'IGNORE',
  'IMPORT',
  'IN',
  'INDEX',
  'INDEXES',
  'INNER',
  'INPATH',
  'INPUTFORMAT',
  'INSERT',
  'INTERSECT',
  'INTERVAL',
  'INTO',
  'IS',
  'ITEMS',
  'JOIN',
  'KEY',
  'KEYS',
  'LAST',
  'LATERAL',
  'LAZY',
  'LEADING',
  'LEFT',
  'LIKE',
  'ILIKE',
  'LIMIT',
  'LINES',
  'LIST',
  'LOAD',
  'LOCAL',
  'LOCATION',
  'LOCK',
  'LOCKS',
  'LOGICAL',
  'MACRO',
  'MAP',
  'MATCHED',
  'MERGE',
  'MINUTE',
  'MINUS',
  'MONTH',
  'MSCK',
  'NAMESPACE',
  'NAMESPACES',
  'NATURAL',
  'NO',
  'NOT',
  'NULL',
  'NULLS',
  'OF',
  'OFFSET',
  'ON',
  'ONLY',
  'OPTION',
  'OPTIONS',
  'OR',
  'ORDER',
  'OUT',
  'OUTER',
  'OUTPUTFORMAT',
  'OVER',
  'OVERLAPS',
  'OVERLAY',
  'OVERWRITE',
  'PARTITION',
  'PARTITIONED',
  'PARTITIONS',
  'PERCENT',
  'PIVOT',
  'PLACING',
  'POSITION',
  'PRECEDING',
  'PRIMARY',
  'PRINCIPALS',
  'PROPERTIES',
  'PURGE',
  'QUALIFY',
  'QUERY',
  'RANGE',
  'RECIPIENT',
  'RECIPIENTS',
  'RECORDREADER',
  'RECORDWRITER',
  'RECOVER',
  'REDUCE',
  'REFERENCES',
  'REFRESH',
  'RENAME',
  'REPAIR',
  'REPEATABLE',
  'REPLACE',
  'RESET',
  'RESPECT',
  'RESTRICT',
  'REVOKE',
  'RIGHT',
  'RLIKE',
  'ROLE',
  'ROLES',
  'ROLLBACK',
  'ROLLUP',
  'ROW',
  'ROWS',
  'SCHEMA',
  'SCHEMAS',
  'SECOND',
  'SELECT',
  'SEMI',
  'SEPARATED',
  'SERDE',
  'SERDEPROPERTIES',
  'SESSION_USER',
  'SET',
  'SETMINUS',
  'SETS',
  'SHOW',
  'SKEWED',
  'SOME',
  'SORT',
  'SORTED',
  'START',
  'STATISTICS',
  'STORED',
  'STRATIFY',
  'STRUCT',
  'SUBSTR',
  'SUBSTRING',
  'SYNC',
  'TABLE',
  'TABLES',
  'TABLESAMPLE',
  'TBLPROPERTIES',
  'TEMPORARY',
  'TERMINATED',
  'THEN',
  'TIME',
  'TO',
  'TOUCH',
  'TRAILING',
  'TRANSACTION',
  'TRANSACTIONS',
  'TRANSFORM',
  'TRIM',
  'TRUE',
  'TRUNCATE',
  'TRY_CAST',
  'TYPE',
  'UNARCHIVE',
  'UNBOUNDED',
  'UNCACHE',
  'UNION',
  'UNIQUE',
  'UNKNOWN',
  'UNLOCK',
  'UNSET',
  'UPDATE',
  'USE',
  'USER',
  'USING',
  'VALUE',
  'VALUES',
  'VIEW',
  'VIEWS',
  'WEEK',
  'WHEN',
  'WHERE',
  'WHICH',
  'WHILE',
  'WINDOW',
  'WITH',
  'WITHIN',
  'WITHOUT',
  'YEAR',
  'ZONE',
];

// Databricks-specific keywords
const databricksKeywords = [
  'CLONE',
  'COPY',
  'DEEP',
  'DELTA',
  'FILES',
  'HISTORY',
  'OPTIMIZE',
  'RESTORE',
  'SHALLOW',
  'VACUUM',
  'VERSION',
  'ZORDER',
];

// Widget-related keywords for notebooks
const widgetKeywords = [
  'CHOICES',
  'COMBOBOX',
  'DROPDOWN',
  'MULTISELECT',
  'TEXT',
  'WIDGET',
];

// All keywords combined
export const keywords = [
  ...sqlKeywords,
  ...databricksKeywords,
  ...widgetKeywords,
];

// Type keywords
export const typeKeywords = [...PRIMITIVE_TYPES, ...COMPOSITE_TYPES];

// Built-in functions (with opening parenthesis for Monaco)
export const builtinFunctions = [
  ...BUILT_IN_FUNCTION_NAMES.map((f) => `${f}(`),
  ...TABLE_VALUED_FUNCTION_NAMES.map((f) => `${f}(`),
];

// Literals
export const literals = ['TRUE', 'FALSE', 'NULL'];

// Remove type keywords and literals from keywords list
export const nonTypeKeywords = keywords.filter(
  (k) => !typeKeywords.includes(k) && !literals.includes(k),
);

export const nonTypeBuiltinFunctions = builtinFunctions;
